import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';

// Lawyers data with descriptions and metrics
const lawyersData = [
  {
    id: 1,
    image: "https://customer-assets.emergentagent.com/job_legal-image-slider/artifacts/z6a2jc88_ChatGPT%20Image%2028%20de%20jan.%20de%202026%2C%2016_57_32.png",
    name: "Débora Pessoa",
    role: "Advogada Especialista em Direito de Família",
    description: "Você está passando por um momento difícil na sua vida familiar? Divórcio, guarda de filhos ou pensão alimentícia? A Dra. Débora Pessoa tem mais de 10 anos de experiência defendendo famílias como a sua. Com sensibilidade e firmeza, ela vai lutar pelos seus direitos e garantir a proteção do que você mais ama.",
    specialties: ["Direito de Família", "Trabalhista", "Civil"],
    metrics: {
      cases: 500,
      successRate: 98,
      experience: 10
    }
  },
  {
    id: 2,
    image: "https://customer-assets.emergentagent.com/job_legal-image-slider/artifacts/m6e2aiam_ChatGPT%20Image%2028%20de%20jan.%20de%202026%2C%2016_55_18.png",
    name: "Guilherme Costa",
    role: "Advogado Especialista em Direito Trabalhista",
    description: "Foi demitido injustamente? Não recebeu suas verbas rescisórias? O Dr. Guilherme Costa é especialista em fazer valer os direitos dos trabalhadores. Com uma abordagem estratégica e conhecimento profundo da legislação, ele já recuperou milhões em indenizações para seus clientes.",
    specialties: ["Trabalhista", "Cível"],
    metrics: {
      cases: 750,
      successRate: 95,
      experience: 12
    }
  },
  {
    id: 3,
    image: "https://customer-assets.emergentagent.com/job_legal-image-slider/artifacts/p1og92eh_ChatGPT%20Image%2028%20de%20jan.%20de%202026%2C%2016_58_59.png",
    name: "Bruno Pirondi",
    role: "Advogado Especialista em Direito Trabalhista",
    description: "Problemas no trabalho? Assédio moral, horas extras não pagas ou acidente de trabalho? O Dr. Bruno Pirondi está pronto para defender você. Com dedicação e experiência comprovada, ele vai garantir que seus direitos sejam respeitados e você receba tudo o que merece.",
    specialties: ["Trabalhista"],
    metrics: {
      cases: 320,
      successRate: 97,
      experience: 8
    }
  },
  {
    id: 4,
    image: "https://customer-assets.emergentagent.com/job_advocacia-site-3/artifacts/idmatqdz_WhatsApp%20Image%202026-02-02%20at%2016.54.29.jpeg",
    name: "Lucas Pessoa",
    role: "Advogado Especialista em Direito Empresarial",
    description: "Precisa de segurança jurídica para o seu negócio? Contratos, franquias ou questões previdenciárias? O Dr. Lucas Pessoa oferece uma assessoria completa para proteger sua empresa e seu patrimônio. Com visão estratégica e experiência multidisciplinar, ele é o parceiro ideal para o crescimento seguro do seu negócio.",
    specialties: ["Empresarial", "Franchising", "Família", "Previdenciário"],
    metrics: {
      cases: 600,
      successRate: 96,
      experience: 15
    }
  }
];

// Animated Counter Component
const AnimatedCounter = ({ value, duration = 1500, prefix = '', suffix = '', trigger }) => {
  const [count, setCount] = useState(0);
  const countRef = useRef(null);

  useEffect(() => {
    // Reset counter when trigger changes
    setCount(0);
    
    const startTime = Date.now();
    const endValue = value;
    
    const animate = () => {
      const now = Date.now();
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Easing function for smooth animation
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      const currentValue = Math.floor(easeOutQuart * endValue);
      
      setCount(currentValue);
      
      if (progress < 1) {
        countRef.current = requestAnimationFrame(animate);
      }
    };
    
    // Small delay before starting animation
    const timeout = setTimeout(() => {
      countRef.current = requestAnimationFrame(animate);
    }, 200);
    
    return () => {
      if (countRef.current) {
        cancelAnimationFrame(countRef.current);
      }
      clearTimeout(timeout);
    };
  }, [value, duration, trigger]);

  return (
    <span className="tabular-nums">
      {prefix}{count}{suffix}
    </span>
  );
};

// Trust Indicator with staggered animation
const TrustIndicator = ({ icon, children, delay, trigger }) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(false);
    const timeout = setTimeout(() => {
      setIsVisible(true);
    }, delay);
    
    return () => clearTimeout(timeout);
  }, [delay, trigger]);

  return (
    <div 
      className={`flex items-center gap-2 text-primary-foreground/70 transition-all duration-500 ease-out
                  ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {icon}
      <span className="font-body text-sm">{children}</span>
    </div>
  );
};

export const TeamSection = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [animationKey, setAnimationKey] = useState(0);

  const nextSlide = useCallback(() => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentSlide((prev) => (prev + 1) % lawyersData.length);
    setAnimationKey(prev => prev + 1);
    setTimeout(() => setIsAnimating(false), 600);
  }, [isAnimating]);

  const prevSlide = useCallback(() => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentSlide((prev) => (prev - 1 + lawyersData.length) % lawyersData.length);
    setAnimationKey(prev => prev + 1);
    setTimeout(() => setIsAnimating(false), 600);
  }, [isAnimating]);

  // Auto-advance carousel every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      nextSlide();
    }, 5000);

    return () => clearInterval(interval);
  }, [nextSlide]);

  const currentLawyer = lawyersData[currentSlide];

  const CheckIcon = () => (
    <svg className="w-5 h-5 text-accent flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
    </svg>
  );

  return (
    <section className="relative w-full bg-primary pt-8 lg:pt-16 pb-16 lg:pb-24 overflow-hidden" id="equipe">
      {/* Top Smoke Transition from Banner */}
      <div className="absolute -top-2 left-0 right-0 h-24 pointer-events-none z-10">
        <div className="absolute inset-x-0 top-0 h-8 bg-primary" />
        <div className="absolute inset-0 bg-gradient-to-b from-primary to-transparent" />
      </div>
      
      {/* Section Header */}
      <div className="container mx-auto px-4 lg:px-8 mb-12 lg:mb-16">
        <div className="text-center">
          <span className="text-accent font-body text-sm font-medium tracking-[0.2em] uppercase mb-3 block">
            Nossa Equipe
          </span>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-primary-foreground font-semibold mb-4">
            Quem vai defender você
          </h2>
          <div className="mx-auto w-20 h-0.5 bg-gradient-to-r from-transparent via-accent to-transparent" />
        </div>
      </div>

      {/* Carousel Container */}
      <div className="container mx-auto px-4 lg:px-8">
        <div className="relative">
          {/* Main Content */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center">
            
            {/* Left Side - Photo and Name */}
            <div className="relative flex flex-col items-center lg:items-start">
              {/* Photo Container */}
              <div className="relative w-full max-w-sm lg:max-w-md overflow-hidden rounded-lg shadow-elegant">
                {lawyersData.map((lawyer, index) => (
                  <div
                    key={lawyer.id}
                    className={`transition-all duration-600 ease-out ${
                      index === currentSlide 
                        ? 'opacity-100 relative' 
                        : 'opacity-0 absolute inset-0'
                    }`}
                  >
                    <div className="aspect-[3/4] w-full">
                      <img
                        src={lawyer.image}
                        alt={lawyer.name}
                        className="w-full h-full object-cover object-center"
                      />
                    </div>
                  </div>
                ))}
                
                {/* Subtle overlay gradient */}
                <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-primary/30 to-transparent pointer-events-none" />
              </div>

              {/* Name and CTA - Below Photo */}
              <div className="mt-6 text-center lg:text-left w-full max-w-sm lg:max-w-md">
                <div key={currentSlide} className="animate-fade-in">
                  <h3 className="font-display text-2xl lg:text-3xl text-primary-foreground font-semibold mb-4">
                    {currentLawyer.name}
                  </h3>
                  <Button 
                    className="bg-accent hover:bg-accent/90 text-primary font-body font-medium px-8 py-3 rounded-full transition-all duration-300 shadow-md hover:shadow-lg"
                  >
                    Agendar Consulta
                  </Button>
                </div>
              </div>
            </div>

            {/* Right Side - Role and Description */}
            <div className="flex flex-col justify-center">
              <div key={currentSlide} className="animate-fade-in">
                {/* Role/Specialty */}
                <div className="mb-6">
                  <span className="inline-block bg-primary-foreground/10 text-primary-foreground font-body text-sm font-medium px-4 py-2 rounded-full mb-4">
                    {currentLawyer.specialties.join(' • ')}
                  </span>
                  <h4 className="font-display text-xl lg:text-2xl xl:text-3xl text-primary-foreground font-semibold leading-tight">
                    {currentLawyer.role}
                  </h4>
                </div>

                {/* Description */}
                <p className="text-primary-foreground/70 font-body text-base lg:text-lg leading-relaxed mb-8">
                  {currentLawyer.description}
                </p>


                {/* Trust Indicators with staggered animation */}
                <div className="flex flex-wrap gap-4">
                  <TrustIndicator 
                    icon={<CheckIcon />} 
                    delay={300}
                    trigger={animationKey}
                  >
                    OAB Ativo
                  </TrustIndicator>
                  
                  <TrustIndicator 
                    icon={<CheckIcon />} 
                    delay={400}
                    trigger={animationKey}
                  >
                    <AnimatedCounter 
                      value={currentLawyer.metrics.cases} 
                      prefix="+" 
                      suffix=" Casos"
                      trigger={animationKey}
                      duration={1500}
                    />
                  </TrustIndicator>
                  
                  <TrustIndicator 
                    icon={<CheckIcon />} 
                    delay={500}
                    trigger={animationKey}
                  >
                    Atendimento Humanizado
                  </TrustIndicator>
                </div>
              </div>
            </div>
          </div>

          {/* Navigation Arrows */}
          <div className="flex justify-center lg:justify-start gap-4 mt-10 lg:mt-12">
            <button
              onClick={prevSlide}
              disabled={isAnimating}
              className="group p-3 rounded-full border border-primary-foreground/20 text-primary-foreground/60 
                       hover:border-accent hover:text-accent hover:bg-accent/10 
                       transition-all duration-300 disabled:opacity-50"
              aria-label="Anterior"
            >
              <ChevronLeft className="w-5 h-5 group-hover:-translate-x-0.5 transition-transform" />
            </button>
            <button
              onClick={nextSlide}
              disabled={isAnimating}
              className="group p-3 rounded-full border border-primary-foreground/20 text-primary-foreground/60 
                       hover:border-accent hover:text-accent hover:bg-accent/10 
                       transition-all duration-300 disabled:opacity-50"
              aria-label="Próximo"
            >
              <ChevronRight className="w-5 h-5 group-hover:translate-x-0.5 transition-transform" />
            </button>
          </div>
        </div>
      </div>

      {/* Decorative Background Elements */}
      <div className="absolute top-0 right-0 w-1/3 h-full opacity-5 pointer-events-none">
        <div className="absolute top-20 right-20 w-64 h-64 rounded-full bg-primary-foreground blur-3xl" />
        <div className="absolute bottom-20 right-40 w-48 h-48 rounded-full bg-accent blur-2xl" />
      </div>
    </section>
  );
};

export default TeamSection;
